import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components/iconify';
import { HEADER, NAV } from 'src/config-global';
import { useBrowserNotification } from 'src/features/Notifications/shared/useBrowserNotification';
import { useLocales } from 'src/locales';
import { useMarkAllNotificationsAsReadQuery } from '../shared/useMarkAllNotificationsAsReadQuery';
import { useNotificationsQuery } from '../shared/useNotificationsQuery';
import { AllNotificationsTab } from './AllNotificationsTab';

type Props = {
  open: boolean;
  onClose: () => void;
};
export function NotificationsDrawer({ open, onClose }: Props) {
  const theme = useTheme();
  const browser = useBrowserNotification();

  const { translate } = useLocales();

  const { markAllNotificationsAsRead } = useMarkAllNotificationsAsReadQuery();

  const { notifications } = useNotificationsQuery();

  const handleClose = () => {
    onClose();
  };

  const onReadAll = async () => {
    await markAllNotificationsAsRead();
  };

  return (
    <StyledNotificationsDrawer
      anchor="right"
      open={open}
      onClose={handleClose}
      BackdropProps={{ invisible: true }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          py: 2,
          px: 2.5,
          borderBottom: `solid 1px ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          {translate('header.notification_dropdown.title')}
        </Typography>

        <Tooltip title={translate('header.notification_dropdown.read_all')}>
          <Box sx={{ position: 'relative' }}>
            <IconButton onClick={onReadAll}>
              <Iconify icon="solar:check-read-outline" />
            </IconButton>
          </Box>
        </Tooltip>

        <IconButton onClick={handleClose}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Stack>

      {!browser.hasPermission() && (
        <Alert severity="warning" variant="soft" sx={{ m: 2.5 }}>
          <AlertTitle>
            {translate('header.notification_dropdown.alert_title')}
          </AlertTitle>
          <Typography>
            {translate('header.notification_dropdown.alert_text_content')}
          </Typography>
          <Button
            color="warning"
            variant="outlined"
            onClick={browser.requestPermission}
            sx={{ mt: 2 }}
          >
            {translate('header.notification_dropdown.alert_button')}
          </Button>
        </Alert>
      )}
      <Stack
        sx={{
          borderTop: `solid 1px ${theme.palette.divider}`,
          flexGrow: 1,
        }}
      >
        <AllNotificationsTab onClose={onClose} items={notifications} />
      </Stack>
    </StyledNotificationsDrawer>
  );
}

const StyledNotificationsDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    top: `${HEADER}px`,
    height: `calc(100dvh - ${HEADER}px - ${HEADER}px)`,
    backgroundColor: theme.palette.background.paper,
    width: NAV.W_BASE * 1.75,
    boxShadow: `-24px 12px 40px 0 ${alpha(
      theme.palette.mode === 'light'
        ? theme.palette.grey[500]
        : theme.palette.common.black,
      0.16,
    )}`,
    [theme.breakpoints.up('md')]: {
      height: `calc(100dvh - ${HEADER}px)`,
    },
  },
}));
